import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { API_BASE_URL } from '../data'

export type GlobalSettings = {
    visit_announced: boolean,
    international_signups_enabled: boolean,
    show_shift_info: boolean,
    use_local_mode: boolean
    // Add other global settings here as they are created
}

type GlobalSettingsContextType = {
    settings: GlobalSettings
    loading: boolean
    error: string | null
    updateSetting: (key: keyof GlobalSettings, value: boolean | string | number) => Promise<void>
    refetch: () => Promise<void>
}

const GlobalSettingsContext = createContext<GlobalSettingsContextType | undefined>(undefined)

type Props = {
    children: ReactNode
}

export const GlobalSettingsProvider = ({ children }: Props) => {
    const [settings, setSettings] = useState<GlobalSettings>({
        visit_announced: false,
        international_signups_enabled: false,
        show_shift_info: false,
        use_local_mode: false
    })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchSettings = async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await fetch(`${API_BASE_URL}/settings`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            })

            if (!response.ok) {
                const contentType = response.headers.get('content-type')
                if (contentType?.includes('application/json')) {
                    const errData = await response.json()
                    throw new Error(errData.message || `Server error: ${response.status}`)
                }
                throw new Error(`Server error: ${response.status} ${response.statusText}`)
            }
           
            const result = await response.json()

            // Convert array of settings to object
            const settingsObj: Record<string, boolean | string | number> = {}
            const settingsArray = Array.isArray(result) ? result : (result.value || [])

            settingsArray.forEach((setting: any) => {
                settingsObj[setting.key] = setting.value
            })

            setSettings({
                visit_announced: settingsObj.visit_announced === true || settingsObj.visit_announced === 'true',
                international_signups_enabled: settingsObj.international_signups_enabled === true || settingsObj.international_signups_enabled === 'true',
                show_shift_info: settingsObj.show_shift_info === true || settingsObj.show_shift_info === 'true',
                use_local_mode: settingsObj.use_local_mode === true || settingsObj.use_local_mode === 'true'
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch settings')
            console.error('Failed to fetch global settings:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchSettings()
    }, [])

    const updateSetting = async (key: keyof GlobalSettings, value: boolean | string | number) => {
        try {
            const response = await fetch(`${API_BASE_URL}/settings/key/${key}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ value: value.toString() })
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to update setting')
            }

            // Update local state
            setSettings(prev => ({
                ...prev,
                [key]: typeof value === 'boolean' ? value : value === 'true'
            }))
        } catch (err) {
            throw err // Let the caller handle the error
        }
    }

    const refetch = async () => {
        await fetchSettings()
    }

    return (
        <GlobalSettingsContext.Provider value={{ settings, loading, error, updateSetting, refetch }}>
            {children}
        </GlobalSettingsContext.Provider>
    )
}

export const useGlobalSettings = () => {
    const context = useContext(GlobalSettingsContext)
    if (context === undefined) {
        throw new Error('useGlobalSettings must be used within a GlobalSettingsProvider')
    }
    return context
}

// Convenience hook for the visit_announced flag
export const useVisitAnnounced = () => {
    const { settings, loading } = useGlobalSettings()
    return { value: settings.visit_announced, loading }
}

// Convenience hook for the international_signups_enabled flag
export const useInternationalSignupsEnabled = () => {
    const { settings, loading } = useGlobalSettings()
    return { value: settings.international_signups_enabled, loading }
}
